import React, { useState } from 'react';
import { toast } from 'sonner';
import { ClientEntity } from '../types';
import { gerarJson } from '../services/geminiService';
import { 
  Target, BarChart2, Brain, Settings, Lightbulb, Save, ArrowUpRight, Users, Eye, 
  MousePointerClick, DollarSign, Activity, Layers, Megaphone,
  TrendingUp, CircleAlert, ChevronDown, Filter, Zap, LayoutGrid, CheckCircle2, Sparkles, Loader2
} from 'lucide-react';
import { 
  AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  BarChart, Bar, Cell
} from 'recharts';

// DADOS MOCKADOS DE ALTA FIDELIDADE
const perfData: any[] = [];

const COLORS = ['#0088FE', '#00C49F', '#FFBB28', '#FF8042'];

const StatCard = ({ title, value, trend, trendUp, prefix = '', suffix = '' }: any) => (
  <div className="bg-white p-5 rounded-2xl border border-gray-100 shadow-sm flex flex-col">
    <span className="text-gray-500 text-sm font-medium mb-2">{title}</span>
    <div className="flex items-end justify-between">
      <span className="text-2xl font-bold text-gray-900">{prefix}{value}{suffix}</span>
      <div className={`flex items-center text-xs font-semibold px-2 py-1 rounded-full ${
        trendUp ? 'bg-emerald-50 text-emerald-600' : 'bg-rose-50 text-rose-600'
      }`}>
        {trendUp ? <ArrowUpRight size={14} className="mr-1" /> : <TrendingUp size={14} className="mr-1 transform rotate-180" />}
        {trend}%
      </div>
    </div>
  </div>
);

const TrafficOverview: React.FC = () => {
  return (
    <div className="space-y-6">
      {/* Objective Tracker */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm flex flex-col md:flex-row gap-8 items-center">
        <div className="flex-1 w-full space-y-3">
          <div className="flex justify-between items-end">
            <div>
              <h3 className="text-lg font-semibold text-gray-900 tracking-tight">Progresso da Meta Mensal</h3>
              <p className="text-sm text-gray-500">Objetivo: - Leads</p>
            </div>
            <div className="text-right">
              <span className="text-2xl font-bold text-gray-900">0</span>
              <span className="text-sm font-medium text-gray-500"> / 0</span>
            </div>
          </div>
          <div className="w-full bg-gray-100 rounded-full h-3 overflow-hidden">
            <div className="bg-blue-600 h-3 rounded-full relative" style={{ width: '0%' }}>
               <div className="absolute top-0 right-0 bottom-0 left-0 bg-white/20 animate-pulse"></div>
            </div>
          </div>
          <p className="text-xs text-gray-400 font-medium text-right">0% Concluído</p>
        </div>
        
        <div className="hidden md:block w-px h-16 bg-gray-100"></div>
        
        <div className="flex-1 w-full grid grid-cols-2 gap-6">
           <div>
             <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Custo por Lead (CPL)</p>
             <div className="flex items-end gap-2">
               <p className="text-xl font-bold text-gray-900">R$ 0,00</p>
               <span className="text-xs font-medium text-gray-500 bg-gray-100 px-2 py-0.5 rounded-full mb-1">Alvo: -</span>
             </div>
           </div>
           <div>
             <p className="text-xs text-gray-500 uppercase font-bold tracking-wider mb-1">Verba Consumida</p>
             <div className="flex items-end gap-2">
               <p className="text-xl font-bold text-gray-900">R$ 0</p>
               <span className="text-xs font-medium text-gray-500 mb-1">/ R$ 0</span>
             </div>
           </div>
        </div>
      </div>

      {/* Resumo de Alto Nível */}
      <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-4 gap-4">
        <StatCard title="Investimento (30d)" value="0,00" prefix="R$ " trend="0" trendUp={true} />
        <StatCard title="Leads Gerados" value="0" trend="0" trendUp={true} />
        <StatCard title="Custo por Lead (CPL)" value="0,00" prefix="R$ " trend="0" trendUp={false} />
        <StatCard title="Conversão de Landing" value="0" suffix="%" trend="0" trendUp={true} />
      </div>

      {/* Gráfico Principal */}
      <div className="bg-white p-6 rounded-2xl border border-gray-100 shadow-sm">
        <div className="flex justify-between items-center mb-6">
          <div>
            <h3 className="text-lg font-semibold text-gray-900 tracking-tight">Performance Diária</h3>
            <p className="text-sm text-gray-500">Volume de tráfego e geração de leads nos últimos 30 dias</p>
          </div>
          <div className="flex gap-2">
            <span className="flex items-center text-xs font-medium text-gray-500"><div className="w-2 h-2 rounded-full bg-blue-500 mr-2"></div>Cliques</span>
            <span className="flex items-center text-xs font-medium text-gray-500"><div className="w-2 h-2 rounded-full bg-emerald-500 mr-2"></div>Leads</span>
          </div>
        </div>
        <div className="h-72">
          {perfData.length > 0 ? (
          <ResponsiveContainer width="100%" height="100%">
            <AreaChart data={perfData} margin={{ top: 10, right: 0, left: -20, bottom: 0 }}>
              <defs>
                <linearGradient id="colorClicks" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.1}/>
                  <stop offset="95%" stopColor="#3b82f6" stopOpacity={0}/>
                </linearGradient>
                <linearGradient id="colorLeads" x1="0" y1="0" x2="0" y2="1">
                  <stop offset="5%" stopColor="#10b981" stopOpacity={0.2}/>
                  <stop offset="95%" stopColor="#10b981" stopOpacity={0}/>
                </linearGradient>
              </defs>
              <CartesianGrid strokeDasharray="3 3" vertical={false} stroke="#f3f4f6" />
              <XAxis dataKey="name" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9ca3af' }} dy={10} />
              <YAxis yAxisId="left" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9ca3af' }} />
              <YAxis yAxisId="right" orientation="right" axisLine={false} tickLine={false} tick={{ fontSize: 12, fill: '#9ca3af' }} />
              <Tooltip 
                contentStyle={{ borderRadius: '12px', border: 'none', boxShadow: '0 4px 6px -1px rgb(0 0 0 / 0.1), 0 2px 4px -2px rgb(0 0 0 / 0.1)' }}
              />
              <Area yAxisId="left" type="monotone" dataKey="clicks" stroke="#3b82f6" strokeWidth={2} fillOpacity={1} fill="url(#colorClicks)" />
              <Area yAxisId="right" type="monotone" dataKey="leads" stroke="#10b981" strokeWidth={2} fillOpacity={1} fill="url(#colorLeads)" />
            </AreaChart>
          </ResponsiveContainer>
          ) : (
            <div className="w-full h-full flex flex-col items-center justify-center text-gray-400 bg-gray-50/50 rounded-xl border border-dashed border-gray-200">
               <Activity size={32} className="mb-2 opacity-20" />
               <p className="text-sm font-medium">Nenhum dado de performance no período.</p>
            </div>
          )}
        </div>
      </div>

      {/* Funil de Marketing */}
      <div className="bg-white overflow-hidden rounded-2xl border border-gray-100 shadow-sm">
        <div className="p-6 border-b border-gray-50">
           <h3 className="text-lg font-semibold text-gray-900 tracking-tight">Funil de Aquisição</h3>
           <p className="text-sm text-gray-500">Métricas de transição do fluxo comercial</p>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-4 divide-y md:divide-y-0 md:divide-x divide-gray-100">
          {[
            { label: 'Impressões', value: '0', sub: 'Anúncios exibidos' },
            { label: 'Cliques', value: '0', sub: 'Visitas na página' },
            { label: 'Leads', value: '0', sub: 'Contatos gerados' },
            { label: 'Vendas', value: '0', sub: 'Negócios fechados' },
          ].map((item, idx) => (
             <div key={idx} className="p-6 text-center hover:bg-gray-50 transition-colors">
                <p className="text-sm font-medium text-gray-500 mb-2">{item.label}</p>
                <p className="text-3xl font-bold text-gray-900 mb-1">{item.value}</p>
                <p className="text-xs text-gray-400">{item.sub}</p>
             </div>
          ))}
        </div>
      </div>
    </div>
  );
};

const CampaignTable = ({ campaigns }: { campaigns: any[] }) => (
  <div className="overflow-x-auto">
    <table className="w-full text-left border-collapse">
      <thead>
        <tr className="border-b border-gray-100 bg-gray-50/50">
           <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider">Campanha</th>
           <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Gasto</th>
           <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">ROAS / CPA</th>
           <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Métricas (CTR / CPC)</th>
           <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-right">Resultados</th>
           <th className="py-3 px-4 text-xs font-semibold text-gray-500 uppercase tracking-wider text-center">Status</th>
        </tr>
      </thead>
      <tbody className="divide-y divide-gray-100">
        {campaigns && campaigns.length > 0 ? campaigns.map((camp, idx) => (
          <tr key={idx} className="hover:bg-gray-50 transition-colors">
            <td className="py-4 px-4">
              <div className="flex items-center gap-3">
                <div className={`w-2 h-2 rounded-full flex-shrink-0 ${camp.status === 'ativo' ? 'bg-emerald-500' : 'bg-gray-300'}`}></div>
                <div>
                  <p className="font-medium text-gray-900 text-sm whitespace-nowrap">{camp.name}</p>
                  <p className="text-xs text-gray-500">{camp.sets} Conjuntos • {camp.ads} Anúncios</p>
                </div>
              </div>
            </td>
            <td className="py-4 px-4 text-right">
              <p className="text-sm font-medium text-gray-900 whitespace-nowrap">R$ {camp.spend}</p>
              <p className="text-xs text-gray-500 whitespace-nowrap">R$ {camp.daily}/dia</p>
            </td>
            <td className="py-4 px-4 text-right">
              <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{camp.cpa}</p>
              <p className="text-xs text-gray-500 whitespace-nowrap">{camp.roas ? `ROAS: ${camp.roas}` : 'CPL Médio'}</p>
            </td>
            <td className="py-4 px-4 text-right">
              <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{camp.ctr || '2.4%'}</p>
              <p className="text-xs text-gray-500 whitespace-nowrap">CPC: R$ {camp.cpc || '1,12'}</p>
            </td>
            <td className="py-4 px-4 text-right">
              <p className="text-sm font-medium text-gray-900 whitespace-nowrap">{camp.results}</p>
              <p className="text-xs text-gray-500 whitespace-nowrap">Leads/Vendas</p>
            </td>
            <td className="py-4 px-4 text-center">
              <span className={`inline-flex px-2.5 py-1 rounded-full text-xs font-medium whitespace-nowrap ${camp.status === 'ativo' ? 'bg-emerald-50 text-emerald-700' : 'bg-gray-100 text-gray-700'}`}>
                {camp.status === 'ativo' ? 'Rodando' : 'Pausada'}
              </span>
            </td>
          </tr>
        )) : (
          <tr>
            <td colSpan={6} className="py-12 text-center text-gray-400 font-medium text-sm">
               Nenhuma campanha ativa neste momento.
            </td>
          </tr>
        )}
      </tbody>
    </table>
  </div>
);

const PlatformDashboard = ({ name, icon: Icon, tabs, colorClass, data }: any) => {
  const [activeTab, setActiveTab] = useState(tabs[0].id);

  return (
    <div className="space-y-6 animate-in fade-in duration-500">
      <div className="flex flex-col sm:flex-row sm:items-center justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className={`w-10 h-10 rounded-xl flex items-center justify-center ${colorClass}`}>
            <Icon size={20} />
          </div>
          <div>
            <h2 className="text-xl font-semibold text-gray-900 tracking-tight">{name}</h2>
            <p className="text-sm text-gray-500">Gestão e análise de performance de campanhas</p>
          </div>
        </div>
        
        {/* Sub-tabs for the platform */}
        <div className="flex bg-gray-100 p-1 rounded-lg w-fit">
          {tabs.map((tab: any) => (
            <button 
              key={tab.id}
              onClick={() => setActiveTab(tab.id)}
              className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all ${activeTab === tab.id ? 'bg-white text-black shadow-sm' : 'text-gray-600 hover:text-gray-900'}`}
            >
              {tab.label}
            </button>
          ))}
        </div>
      </div>

      <div className="bg-white rounded-2xl border border-gray-100 shadow-sm overflow-hidden">
        <div className="p-6 border-b border-gray-100 flex justify-between items-center">
            <div>
              <h3 className="text-lg font-semibold text-gray-900">{tabs.find((t:any) => t.id === activeTab)?.title}</h3>
              <p className="text-sm text-gray-500">{tabs.find((t:any) => t.id === activeTab)?.desc}</p>
            </div>
            <button className="text-gray-400 hover:text-gray-900 transition-colors">
              <Filter size={18} />
            </button>
        </div>
        
        <CampaignTable campaigns={data[activeTab] || []} />
      </div>
    </div>
  );
};

const metaTabs = [
  { id: 'escala', label: 'Escala', title: 'Campanhas de Escala', desc: 'Maior investimento em públicos validados' },
  { id: 'teste', label: 'Testes', title: 'Laboratório de Testes', desc: 'Validação de novos públicos e criativos' },
  { id: 'remarketing', label: 'Remarketing', title: 'Remarketing', desc: 'Resgate de abandonos e interessados' },
];

const googleTabs = [
  { id: 'intencao', label: 'Alta Intenção', title: 'Rede de Pesquisa', desc: 'Fundo de funil e alta intenção de busca' },
  { id: 'marca', label: 'Marca', title: 'Institucional', desc: 'Proteção do nome da empresa ' },
];

const mock_meta_data = {
  escala: [],
  teste: [],
  remarketing: []
};

const mock_google_data = {
  intencao: [],
  marca: []
};

const CreativesDashboard = () => {
  const [activeCategory, setActiveCategory] = useState('dor');
  const [activeFormat, setActiveFormat] = useState('todos');

  const categories = [
    { id: 'dor', label: 'Dor e Solução', count: 0 },
    { id: 'desejo', label: 'Desejo/Aspiração', count: 0 },
    { id: 'prova', label: 'Prova Social', count: 0 },
    { id: 'oferta', label: 'Oferta Direta', count: 0 },
    { id: 'urgencia', label: 'Escassez/Urgência', count: 0 },
  ];

  const formats = [
    { id: 'todos', label: 'Todos' },
    { id: 'video', label: 'Vídeos (Reels/TikTok)' },
    { id: 'imagem', label: 'Imagens Estáticas' },
    { id: 'carrossel', label: 'Carrossel' },
  ];

  const mockCreatives: any[] = [];

  const filteredCreatives = mockCreatives.filter(c => 
    c.category === activeCategory && 
    (activeFormat === 'todos' || c.format === activeFormat)
  );

  return (
    <div className="space-y-6">
      <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
        <div>
           <h2 className="text-xl font-semibold text-gray-900 tracking-tight">Biblioteca de Criativos</h2>
           <p className="text-sm text-gray-500">Organizados por ângulo de comunicação e formato.</p>
        </div>
        <button className="text-sm font-medium text-blue-600 hover:text-blue-700 bg-blue-50 px-4 py-2 rounded-lg transition-colors w-fit">
          Adicionar Criativo
        </button>
      </div>
      
      <div className="flex flex-col md:flex-row gap-8 items-start">
        {/* Sidebar Categorias */}
        <div className="w-full md:w-64 flex-shrink-0 space-y-1 bg-white p-2 rounded-xl border border-gray-100 shadow-sm">
          {categories.map(cat => (
            <button
              key={cat.id}
              onClick={() => setActiveCategory(cat.id)}
              className={`w-full flex items-center justify-between px-4 py-3 rounded-lg text-sm font-medium transition-colors ${
                activeCategory === cat.id ? 'bg-gray-900 text-white shadow-md' : 'text-gray-600 hover:bg-gray-50 hover:text-gray-900'
              }`}
            >
              <span>{cat.label}</span>
              <span className={`text-xs px-2 py-0.5 rounded-full ${activeCategory === cat.id ? 'bg-gray-700 text-white' : 'bg-gray-100 text-gray-500'}`}>
                {cat.count}
              </span>
            </button>
          ))}
        </div>

        {/* Conteúdo Principal */}
        <div className="flex-1 w-full space-y-6">
          {/* Sub-tabs de formato */}
          <div className="flex bg-white p-1 rounded-lg w-fit border border-gray-100 shadow-sm overflow-x-auto max-w-full">
            {formats.map(fmt => (
              <button
                key={fmt.id}
                onClick={() => setActiveFormat(fmt.id)}
                className={`px-4 py-1.5 rounded-md text-sm font-medium transition-all whitespace-nowrap ${
                  activeFormat === fmt.id ? 'bg-gray-100 text-gray-900' : 'text-gray-500 hover:text-gray-900'
                }`}
              >
                {fmt.label}
              </button>
            ))}
          </div>

          {/* Grid de Criativos */}
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
            {filteredCreatives.length > 0 ? (
              filteredCreatives.map((creative) => (
                <div key={creative.id} className="bg-white rounded-2xl border border-gray-100 overflow-hidden shadow-sm group hover:shadow-md transition-all">
                  <div className="h-40 bg-gray-100 relative overflow-hidden">
                    <img src={creative.img} alt="Creative preview" className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700" />
                    <div className="absolute top-3 left-3 bg-white/90 backdrop-blur text-gray-900 text-[10px] font-bold px-2 py-1 rounded shadow-sm flex items-center gap-1">
                       <span className={`w-1.5 h-1.5 rounded-full ${creative.status === 'ativo' ? 'bg-emerald-500' : 'bg-gray-400'}`}></span>
                       {creative.format.toUpperCase()}
                    </div>
                  </div>
                  <div className="p-4">
                    <h4 className="font-semibold text-gray-900 mb-1 line-clamp-1">{creative.title}</h4>
                    <p className="text-xs text-gray-500 mb-4 line-clamp-1 flex items-center gap-1">
                      <Target size={12} />
                      {creative.campaign}
                    </p>
                    <div className="grid grid-cols-3 gap-2 p-3 bg-gray-50 rounded-xl border border-gray-100/50">
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">CTR</p>
                        <p className="text-sm font-semibold text-emerald-600">{creative.ctr}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">CPA</p>
                        <p className="text-sm font-semibold text-gray-900">{creative.cpa}</p>
                      </div>
                      <div>
                        <p className="text-[10px] text-gray-400 uppercase font-medium mb-0.5">Gasto</p>
                        <p className="text-sm font-semibold text-gray-600">{creative.spend}</p>
                      </div>
                    </div>
                  </div>
                </div>
              ))
            ) : (
              <div className="col-span-full py-12 text-center bg-gray-50 rounded-2xl border border-dashed border-gray-200">
                 <p className="text-gray-500 text-sm">Nenhum criativo encontrado para este filtro.</p>
              </div>
            )}
          </div>
        </div>
      </div>
    </div>
  );
};


const AiAdGenerator = ({ client }: { client: ClientEntity }) => {
  const [loading, setLoading] = useState(false);
  const [publishing, setPublishing] = useState(false);
  const [generatedData, setGeneratedData] = useState<any>(null);

  const generateAds = async () => {
    setLoading(true);
    try {
      // Pelo contrato, não pelo host Express: `/api/generate-ads` mantinha um
      // segundo cliente do Gemini lendo `process.env.GEMINI_API_KEY` direto, e
      // esse gasto não entrava em relatório nenhum.
      const data = await gerarJson<any>(
        `Você é um especialista em Google Ads de alta performance.
Preciso criar uma campanha de Pesquisa no Google Ads para o seguinte cliente:

Nome da Empresa: ${client.data.name}
Nicho: ${client.data.categories?.[0] || 'Geral'}
Resumo do Negócio: ${client.data.summary || ''}

Gere exatamente:
1. 3 opções de Títulos (máximo 30 caracteres cada).
2. 2 opções de Descrições (máximo 90 caracteres cada).
3. 10 palavras-chave focadas em conversão (intenção de compra).

Retorne APENAS um objeto JSON válido, sem markdown, sem crases, neste formato exato:
{
  "titles": ["titulo1", "titulo2", "titulo3"],
  "descriptions": ["desc1", "desc2"],
  "keywords": ["kw1", "kw2", "kw3", "..."]
}`,
        client.id,
      );

      setGeneratedData(data);
      toast.success('Anúncios gerados com sucesso!');
    } catch (err: any) {
      toast.error('Erro ao gerar anúncios: ' + err.message);
    } finally {
      setLoading(false);
    }
  };

  const publishCampaign = async () => {
    setPublishing(true);
    try {
      const response = await fetch('/api/publish-campaign', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({
          clientName: client.data.name,
          campaignData: generatedData
        })
      });
      const data = await response.json();
      if (data.error) throw new Error(data.error);
      
      toast.success(data.message);
    } catch (err: any) {
      toast.error('Erro ao publicar campanha: ' + err.message);
    } finally {
      setPublishing(false);
    }
  };

  return (
    <div className="bg-white p-6 rounded-2xl border border-indigo-100 shadow-sm">
      <div className="flex flex-col md:flex-row justify-between items-start md:items-center mb-6 gap-4">
        <div>
          <h3 className="text-xl font-bold text-gray-900 flex items-center gap-2">
            <Sparkles className="text-indigo-600" size={24} />
            Gerador de Campanhas IA
          </h3>
          <p className="text-sm text-gray-500 mt-1">
            Crie anúncios de alta conversão para o Google Ads automaticamente usando IA, baseados nos dados do cliente.
          </p>
        </div>
        {!generatedData && (
          <button
            onClick={generateAds}
            disabled={loading}
            className="bg-indigo-600 hover:bg-indigo-700 text-white px-5 py-2.5 rounded-lg font-medium transition-all flex items-center gap-2 disabled:opacity-70"
          >
            {loading ? <Loader2 size={18} className="animate-spin" /> : <Sparkles size={18} />}
            Gerar Anúncios Agora
          </button>
        )}
      </div>

      {loading && (
        <div className="py-12 flex flex-col items-center justify-center text-indigo-600">
          <Loader2 size={40} className="animate-spin mb-4" />
          <p className="font-medium animate-pulse">A IA está analisando a empresa e criando a copy perfeita...</p>
        </div>
      )}

      {generatedData && !loading && (
        <div className="animate-in fade-in slide-in-from-bottom-4 duration-500 space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
            <div className="space-y-4">
              <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100/50">
                <h4 className="font-semibold text-indigo-900 mb-3 text-sm uppercase tracking-wider">Títulos Otimizados (Máx 30 char)</h4>
                <ul className="space-y-2">
                  {generatedData.titles?.map((title: string, i: number) => (
                    <li key={i} className="bg-white p-3 rounded-lg border border-indigo-100 text-sm font-medium text-gray-800 shadow-sm flex items-center justify-between">
                      {title}
                      <span className="text-[10px] text-gray-400">{title.length}/30</span>
                    </li>
                  ))}
                </ul>
              </div>
              
              <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100/50">
                <h4 className="font-semibold text-indigo-900 mb-3 text-sm uppercase tracking-wider">Descrições Persuasivas (Máx 90 char)</h4>
                <ul className="space-y-2">
                  {generatedData.descriptions?.map((desc: string, i: number) => (
                    <li key={i} className="bg-white p-3 rounded-lg border border-indigo-100 text-sm text-gray-700 shadow-sm">
                      {desc}
                      <div className="text-[10px] text-gray-400 mt-1 text-right">{desc.length}/90</div>
                    </li>
                  ))}
                </ul>
              </div>
            </div>

            <div className="bg-indigo-50/50 p-4 rounded-xl border border-indigo-100/50">
              <h4 className="font-semibold text-indigo-900 mb-3 text-sm uppercase tracking-wider">Palavras-chave (Fundo de Funil)</h4>
              <div className="flex flex-wrap gap-2">
                {generatedData.keywords?.map((kw: string, i: number) => (
                  <span key={i} className="bg-white border border-indigo-200 text-indigo-700 px-3 py-1.5 rounded-full text-xs font-semibold shadow-sm">
                    {kw}
                  </span>
                ))}
              </div>
            </div>
          </div>

          <div className="pt-6 border-t border-gray-100 flex justify-end gap-3">
            <button 
              onClick={() => setGeneratedData(null)}
              className="px-5 py-2.5 rounded-lg font-medium text-gray-600 hover:bg-gray-100 transition-colors"
            >
              Descartar e Recriar
            </button>
            <button
              onClick={publishCampaign}
              disabled={publishing}
              className="bg-emerald-600 hover:bg-emerald-700 text-white px-6 py-2.5 rounded-lg font-bold transition-all flex items-center gap-2 disabled:opacity-70 shadow-sm"
            >
              {publishing ? <Loader2 size={18} className="animate-spin" /> : <Target size={18} />}
              {publishing ? 'Publicando...' : 'Aprovar e Publicar no Google Ads'}
            </button>
          </div>
        </div>
      )}
    </div>
  );
};


const CommandCenter = ({ client }: { client: ClientEntity }) => {
  const [dailyBudget, setDailyBudget] = useState(30);
  const [monthlyCap, setMonthlyCap] = useState(900);
  const [saving, setSaving] = useState(false);

  const handleSave = () => {
    setSaving(true);
    setTimeout(() => {
      setSaving(false);
      toast.success('Orçamento atualizado no Google Ads com sucesso!');
    }, 1500);
  };

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 lg:grid-cols-3 gap-6">
        
        {/* Bloco 1: Controle Manual */}
        <div className="bg-white p-6 rounded-2xl border border-gray-200 shadow-sm flex flex-col">
          <div className="flex items-center gap-3 mb-6">
            <div className="p-2 bg-gray-100 rounded-lg text-gray-700">
              <Settings size={20} />
            </div>
            <div>
              <h3 className="font-bold text-gray-900">Orçamento Atual</h3>
              <p className="text-xs text-gray-500">Controle manual da verba</p>
            </div>
          </div>
          
          <div className="space-y-6 flex-1">
            <div>
              <div className="flex justify-between items-end mb-2">
                <label className="text-sm font-medium text-gray-700">Diário</label>
                <span className="text-lg font-bold text-gray-900">R$ {dailyBudget},00</span>
              </div>
              <input 
                type="range" 
                min="10" max="200" step="10" 
                value={dailyBudget} 
                onChange={(e) => setDailyBudget(Number(e.target.value))}
                className="w-full accent-black"
              />
            </div>
            
            <div>
              <div className="flex justify-between items-end mb-2">
                <label className="text-sm font-medium text-gray-700">Teto Mensal</label>
                <span className="text-lg font-bold text-gray-900">R$ {monthlyCap},00</span>
              </div>
              <input 
                type="range" 
                min="300" max="6000" step="100" 
                value={monthlyCap} 
                onChange={(e) => setMonthlyCap(Number(e.target.value))}
                className="w-full accent-black"
              />
            </div>
          </div>
          
          <button 
            onClick={handleSave}
            disabled={saving}
            className="w-full mt-6 bg-black hover:bg-gray-900 text-white py-2.5 rounded-lg font-medium transition-colors flex items-center justify-center gap-2"
          >
            {saving ? <Loader2 size={16} className="animate-spin" /> : <Save size={16} />}
            {saving ? 'Salvando...' : 'Salvar Alterações'}
          </button>
        </div>

        {/* Bloco 2: Cérebro Nyroh (O Recomendado) */}
        <div className="bg-indigo-50 p-6 rounded-2xl border border-indigo-100 shadow-sm flex flex-col relative overflow-hidden">
          <div className="absolute -top-10 -right-10 text-indigo-500/10">
            <Brain size={150} />
          </div>
          <div className="flex items-center gap-3 mb-4 relative z-10">
            <div className="p-2 bg-indigo-600 rounded-lg text-white">
              <Brain size={20} />
            </div>
            <div>
              <h3 className="font-bold text-indigo-950">Cérebro Nyroh</h3>
              <p className="text-xs text-indigo-700 font-medium">Recomendação Baseada em IA</p>
            </div>
          </div>
          
          <div className="flex-1 relative z-10">
            <p className="text-sm text-indigo-900 leading-relaxed mb-4">
              Baseado no seu perfil de <strong>{client.data.categories?.[0] || 'Negócio Local'}</strong> e no custo médio da sua região, 
              a Nyroh recomenda um investimento de <strong className="text-indigo-700 text-lg">R$ 50/dia</strong> focado em receber mensagens no WhatsApp. 
              Isso deve gerar entre <strong className="text-indigo-700">5 a 10 contatos</strong> por dia.
            </p>
          </div>
          
          <button 
            onClick={() => { setDailyBudget(50); setMonthlyCap(1500); toast.success('Recomendação da IA aplicada com sucesso!'); }}
            className="w-full mt-4 bg-indigo-600 hover:bg-indigo-700 text-white py-2.5 rounded-lg font-bold transition-colors shadow-md flex items-center justify-center gap-2 relative z-10"
          >
            <Sparkles size={16} />
            Aplicar Recomendação da IA
          </button>
        </div>

        {/* Bloco 3: Central de Sugestões (O Sinal Amarelo) */}
        <div className="bg-amber-50 p-6 rounded-2xl border border-amber-100 shadow-sm flex flex-col">
          <div className="flex items-center gap-3 mb-4">
            <div className="p-2 bg-amber-400 rounded-lg text-amber-900">
              <Lightbulb size={20} />
            </div>
            <div>
              <h3 className="font-bold text-amber-950">Central de Oportunidades</h3>
              <p className="text-xs text-amber-700 font-medium">Ações que exigem sua aprovação</p>
            </div>
          </div>
          
          <div className="flex-1 flex flex-col justify-center">
            <div className="bg-white p-4 rounded-xl border border-amber-200 shadow-sm">
              <div className="flex items-start gap-2 mb-3">
                <div className="w-2 h-2 rounded-full bg-amber-500 mt-1.5 animate-pulse shrink-0"></div>
                <p className="text-sm text-amber-950 font-medium">A palavra "{client.data.categories?.[0] || 'Serviço'}" está trazendo clientes a R$ 2,00 (muito barato!). Sugerimos aumentar a verba dela em R$ 10,00.</p>
              </div>
              <div className="flex gap-2">
                <button onClick={() => toast.success('Sugestão aprovada! Verba otimizada.')} className="flex-1 bg-amber-500 hover:bg-amber-600 text-white py-1.5 rounded-md text-xs font-bold transition-colors">Aprovar</button>
                <button onClick={() => toast.info('Sugestão recusada.')} className="flex-1 bg-white hover:bg-amber-50 text-amber-700 border border-amber-200 py-1.5 rounded-md text-xs font-bold transition-colors">Recusar</button>
              </div>
            </div>
          </div>
        </div>
        
      </div>
    </div>
  );
};

export const TrafficManagement: React.FC<{ client: ClientEntity }> = ({ client }) => {
  const [activeTab, setActiveTab] = useState<'comando' | 'geral' | 'meta' | 'google' | 'ai'>('comando');

  return (
    <div className="h-full flex flex-col w-full max-w-6xl mx-auto pb-12">
      <div className="mb-8">
        <h2 className="text-3xl font-semibold text-gray-900 tracking-tight">Tráfego Pago</h2>
        <p className="text-gray-500 mt-1">Acompanhe a performance e o retorno sobre o investimento em tempo real.</p>
      </div>

      {/* Main Tabs */}
      <div className="flex bg-white p-1 rounded-xl w-fit mb-8 shadow-sm border border-gray-100">
        {[
          { id: 'comando', label: 'Estratégia & Orçamento', icon: Brain },
          { id: 'geral', label: 'Visão Geral', icon: Activity },
          { id: 'meta', label: 'Meta Ads', icon: Target },
          { id: 'google', label: 'Google Ads', icon: Target },
          { id: 'ai', label: 'Criador IA', icon: Sparkles },
        ].map(item => (
          <button 
            key={item.id}
            onClick={() => setActiveTab(item.id as any)}
            className={`flex items-center gap-2 px-6 py-3 rounded-lg text-sm font-medium transition-all ${
              activeTab === item.id ? 'bg-gray-900 text-white shadow-md' : 'text-gray-600 hover:text-gray-900 hover:bg-gray-50'
            }`}
          >
            <item.icon size={16} className={activeTab === item.id ? 'text-white' : 'text-gray-400'} />
            {item.label}
          </button>
        ))}
      </div>

      <div className="animate-in fade-in slide-in-from-bottom-4 duration-500">
        {activeTab === 'comando' && <CommandCenter client={client} />}
        {activeTab === 'geral' && <TrafficOverview />}
        {activeTab === 'meta' && <PlatformDashboard name="Meta Ads" icon={Target} tabs={metaTabs} data={mock_meta_data} colorClass="bg-blue-50 text-blue-600" />}
        {activeTab === 'google' && <PlatformDashboard name="Google Ads" icon={Target} tabs={googleTabs} data={mock_google_data} colorClass="bg-red-50 text-red-600" />}
        {activeTab === 'ai' && <AiAdGenerator client={client} />}
      </div>
    </div>
  );
};
